// src/components/featured/Featured.tsx
// Rediseño corporativo estilo Notion/Linear
import { useState, useContext } from "react";
import { Box, Typography, Stack, IconButton, Menu, MenuItem, Dialog, DialogContent, TextField, Button, alpha, Tooltip } from "@mui/material";
import { MoreVert, Refresh, Edit, Today, TrendingUp, TrendingDown, TrendingFlat, Close as CloseIcon, Flag } from "@mui/icons-material";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useSalesMetrics } from "../../hooks/useSalesMetrics";
import { DashboardService } from "../../services/api/dashboardService";
import { DarkModeContext } from "../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F8FAFC", bgT: "#F1F5F9", text: "#0F172A", textS: "#475569", textT: "#64748B", border: "#E2E8F0", accent: "#0369A1", accentBg: "#E0F2FE", success: "#15803D", successBg: "#DCFCE7", warning: "#B45309", warningBg: "#FEF3C7", error: "#DC2626", errorBg: "#FEE2E2", shadow: "0 1px 2px rgba(15,23,42,0.06)", hover: "rgba(15,23,42,0.04)" },
  dark: { bg: "#0F172A", bgS: "#111827", bgT: "#1E293B", text: "#E2E8F0", textS: "#94A3B8", textT: "#64748B", border: "#1E293B", accent: "#38BDF8", accentBg: "#0C4A6E", success: "#4ADE80", successBg: "#14352A", warning: "#FBBF24", warningBg: "#3A2E12", error: "#F87171", errorBg: "#3D2020", shadow: "0 1px 3px rgba(0,0,0,0.5)", hover: "rgba(255,255,255,0.04)" },
};

interface FeaturedProps { sucursalId?: number; }

const Featured: React.FC<FeaturedProps> = ({ sucursalId = 1 }) => {
  const metrics = useSalesMetrics(sucursalId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newTarget, setNewTarget] = useState("");
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const handleUpdateTarget = async () => {
    try { if (sucursalId) { await DashboardService.updateSucursalTarget(sucursalId, Number(newTarget)); window.location.reload(); } }
    catch { } setOpenDialog(false);
  };

  const fmt = (v: number) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(v);

  const getColor = (pct: number) => { if (pct >= 100) return p.success; if (pct >= 50) return p.warning; return p.error; };
  const getTrend = (a: number, b: number) => {
    if (a > b) return <TrendingUp sx={{ fontSize: 14, color: p.success }} />;
    if (a < b) return <TrendingDown sx={{ fontSize: 14, color: p.error }} />;
    return <TrendingFlat sx={{ fontSize: 14, color: p.textT }} />;
  };

  if (metrics.isLoading) return (
    <Box sx={{ p: 3, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
      <Typography sx={{ color: p.textT, fontSize: "0.84rem" }}>Cargando...</Typography>
    </Box>
  );

  const pColor = getColor(metrics.targetPercentage);

  return (
    <Box sx={{ p: 3, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, boxShadow: p.shadow }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}><Today sx={{ fontSize: 18 }} /></Box>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 650, color: p.text }}>Ingresos del Día</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: p.textT }}>Ventas realizadas hoy</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Actualizar"><IconButton onClick={() => window.location.reload()} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.textS, "&:hover": { bgcolor: p.bgT } }}><Refresh sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Opciones"><IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}><MoreVert sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Stack>
      </Stack>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, boxShadow: p.shadow } }}>
        <MenuItem onClick={() => { setOpenDialog(true); setAnchorEl(null); setNewTarget(metrics.target.toString()); }} sx={{ fontSize: "0.84rem", gap: 1 }}><Edit sx={{ fontSize: 16 }} /> Actualizar Objetivo</MenuItem>
      </Menu>

      {/* Progress + Total */}
      <Stack direction="row" spacing={4} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ width: 130, height: 130, position: "relative" }}>
          <CircularProgressbar value={metrics.targetPercentage} text={`${Math.round(metrics.targetPercentage)}%`} strokeWidth={8}
            styles={buildStyles({ pathColor: pColor, textColor: pColor, trailColor: darkMode ? "#333" : "#E9E9E7", pathTransitionDuration: 1.5, textSize: "1.3rem" })} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: metrics.targetPercentage >= 100 ? p.successBg : p.warningBg, color: metrics.targetPercentage >= 100 ? p.success : p.warning, fontSize: "0.75rem", fontWeight: 600, mb: 1.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: metrics.targetPercentage >= 100 ? p.success : p.warning }} />
            {metrics.targetPercentage >= 100 ? "Objetivo Alcanzado" : "En Progreso"}
          </Box>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Total de Hoy</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(metrics.dailyTotal)}</Typography>
        </Box>
      </Stack>

      {/* Devoluciones */}
      {metrics.dailyReturns > 0 && (
        <Box sx={{ p: 2, borderRadius: "10px", bgcolor: p.errorBg, border: `1px solid ${alpha(p.error, 0.2)}`, mb: 3 }}>
          <Stack spacing={0.8}>
            <Stack direction="row" justifyContent="space-between"><Typography sx={{ fontSize: "0.78rem", color: p.textS }}>Ventas Brutas</Typography><Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(metrics.dailyGrossTotal || 0)}</Typography></Stack>
            <Stack direction="row" justifyContent="space-between"><Typography sx={{ fontSize: "0.78rem", color: p.error }}>Devoluciones</Typography><Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: p.error, fontFamily: "'IBM Plex Mono', monospace" }}>-{fmt(metrics.dailyReturns || 0)}</Typography></Stack>
            <Box sx={{ borderTop: `1px solid ${alpha(p.error, 0.2)}`, pt: 0.8 }}>
              <Stack direction="row" justifyContent="space-between"><Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: p.text }}>Total Neto</Typography><Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(metrics.dailyTotal)}</Typography></Stack>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Metric Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
        {[
          { label: "Objetivo", value: metrics.target, compare: metrics.target, icon: <Flag sx={{ fontSize: 14 }} />, footer: `${Math.round(metrics.targetPercentage)}% completado` },
          { label: "Semana Anterior", value: metrics.previousWeekTotal, compare: metrics.previousWeekTotal, footer: `${metrics.dailyTotal > metrics.previousWeekTotal ? "+" : ""}${fmt(metrics.dailyTotal - metrics.previousWeekTotal)}` },
          { label: "Mes Anterior", value: metrics.previousMonthTotal, compare: metrics.previousMonthTotal, footer: `${metrics.dailyTotal > metrics.previousMonthTotal ? "+" : ""}${fmt(metrics.dailyTotal - metrics.previousMonthTotal)}` },
        ].map((m, i) => (
          <Box key={i} sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bgS }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" }}>{m.label}</Typography>
              {getTrend(metrics.dailyTotal, m.compare)}
            </Stack>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace", mb: 0.5 }}>{fmt(m.value)}</Typography>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: metrics.dailyTotal >= m.compare ? p.success : p.error }}>{m.footer}</Typography>
          </Box>
        ))}
      </Box>

      {/* Target Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg } }}>
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: p.text }}>Actualizar Objetivo</Typography>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.textT }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.5 }}>Nuevo Objetivo</Typography>
          <TextField fullWidth size="small" type="number" value={newTarget} onChange={(e) => setNewTarget(e.target.value)} sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bgS, fontFamily: "'IBM Plex Mono', monospace" } }} />
        </DialogContent>
        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px" }}>Cancelar</Button>
          <Button variant="contained" disableElevation onClick={handleUpdateTarget} sx={{ bgcolor: p.accent, color: "#FFF", fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px" }}>Actualizar</Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Featured;
